<template>
  <div>
    <h3>Filtre</h3>
    <div class="panel panel-default">
      <div class="panel-body">
        <slot>
        </slot>
        <div class="row">
          <div class="col-xs-12">
            <btn type="success" @click="filterHelp = !filterHelp" size="xs">
              {{ filterHelp ? `Replier l'aide` : `En savoir plus` }}
            </btn>
            <btn type="info" @click="variablesCollapse = !variablesCollapse"
                 :style="{ 'margin-left': '1rem' }"
                 size="xs">
              {{ variablesCollapse ? 'Masquer ' : 'Lister ' }} les variables
            </btn>
          </div>
        </div>
        <collapse v-model="filterHelp">
          <div>
            <h4>Expression et variables</h4>
            <p>
              Pour ajouter un filtre, il vous faut saisir une expression dans
              le champ texte en-dessous.
            </p>
            <p>
              Une expression doit avoir la forme suivante : Variable Opérateur
              Valeur.<br>
              <small>
                <i>
                  Exemples : $sexe = 1, $pe : C2, $origine &lt; 2, etc.
                </i>
              </small>
            </p>
            <p>
              <ul>
                <li>
                  Une <strong>variable</strong> commence par un «$» et
                  correspond à une donnée de l'infection.<br>
                  <small>
                    <i>
                      Exemples : $sexe, $origine, $pe, etc.
                    </i>
                  </small>
                </li>
                <li>
                  Un <strong>opérateur</strong> permet de comparer la variable
                  et la valeur.<br>
                  <small>
                    <i>
                      Exemples : $sexe = 2 (uniquement les femmes), $origine = 2
                      (patients provenant de réanimation), etc.
                    </i>
                  </small>
                </li>
                <li>
                  Les <strong>valeurs</strong> correspondent au valeurs
                  remplies dans les fiches.
                  <br>
                  <small>
                    <i>
                      Exemples : 1 pour sexe masculin, 2 pour sexe féminin, 3
                      pour autre, etc.
                    </i>
                  </small>
                </li>
              </ul>
            </p>
            <h4>Valeurs numériques</h4>
            <p>
              Les filtres pour les valeurs numériques s'écrivent en utilisant
              les opérateurs <span class="operator">=</span>,
              <span class="operator">&gt;</span>,
              <span class="operator">&lt;</span>,
              <span class="operator">&gt;=</span> et
              <span class="operator">&lt;=</span>
            </p>
            <p>
              <u>Exemples :</u>
              <ul>
                <li><b>$sexe = 2</b> -&gt; Patients de sexe féminin</li>
                <li>
                  <b>poids &lt; 1000</b> -&gt;
                  Bébé avec un poids de naissance inférieur à 1kg
                </li>
                <li>
                  <b>$cancer &lt;= 2</b> -&gt;
                  Patients avec tumeur ou hémopathie
                </li>
              </ul>
            </p>
            <h4>Valeurs «textes»</h4>
            <p>
              Les filtres pour les valeurs «textes» (par exemple les portes
              d'entrées ou les micro-organismes) s'écrivent en utilisant
              les opérateurs <span class="operator">:</span> et
              <span class="operator">~</span>.
            </p>
            <p>
              <strong>Attention !</strong> Si la valeur attendue contient des
              espaces, il faut l'encadrer par des guillemets.
            </p>
            <table class="table table-responsive">
              <tbody>
                <tr>
                  <td><span class="operator">:</span></td>
                  <td>
                    <p>
                      Sélectionne les fiches dont le champ est
                      <strong>strictement identique</strong> à la valeur
                      saisie.
                    </p>
                    <p>
                      <u>Exemples :</u>
                      <ul>
                        <li>
                          <b>$pe : C1 ou $pe : A3</b> -&gt; Porte d'entrée CVC ou urinaire
                        </li>
                        <li>
                          <b>$mo : "STA AUR"</b> -&gt; Staphylocoques dorés
                        </li>
                      </ul>
                    </p>
                  </td>
                </tr>
                <tr>
                  <td>
                    <span class="operator">~</span>
                  </td>
                  <td>
                    <p>
                      Sélectionne les fiches dont le champ
                      <strong>contient</strong> la valeur saisie.
                    </p>
                    <p>
                      <u>Exemples :</u>
                      <ul>
                        <li>
                          <b>$pe ~ C ou $pe : D1</b> -&gt; Porte d'entrée cathéter (C1 à C13 + D1)
                        </li>
                        <li>
                          <b>$mo ~ STA</b> -&gt; Staphylocoques
                        </li>
                        <li>
                          <b>$mo ~ "STA A"</b> -&gt; Staphylocoques dorés et autre
                        </li>
                      </ul>
                    </p>
                  </td>
                </tr>
              </tbody>
            </table>
            <p>
              Afin de sélectionner plusieurs valeurs sans avoir à écrire de
              longues lignes, il est également possible d'utiliser le caractère
              <span class="operator">.</span>. Ce caractère fonctionne comme un
              joker et sélectionnera toutes les valeurs possibles.
            </p>
            <p>
              <u>Exemples :</u>
              <ul>
                <li>
                  <b>$pe : "C."</b> -&gt; Porte d'entrée C1 à C9
                </li>
                <li>
                  <b>$pe : "C1."</b> -&gt; Porte d'entrée C10 à C13
                </li>
              </ul>
            </p>
            <h4>Autres opérateurs</h4>
            <table class="table table-responsive">
              <tbody>
                <tr>
                  <td>
                    <span class="operator">et</span>
                  </td>
                  <td>
                    Sélectionne les fiches remplissant les deux critères. <br/>
                    Identique au filtre Excel.<br/><br/>
                    <u>Exemples :</u>
                    <ul>
                      <li>
                        <b>$mo : "STA AUR" et $pe : C1</b> -&gt;
                        Les Staphylocoques dorés avec porte d'entrée CVC
                      </li>
                      <li>
                        <b>$sexe = 1 et $poids &lt; 1000</b> -&gt;
                        Les bébés garçons de moins d'1kg
                      </li>
                    </ul>
                  </td>
                </tr>
                <tr>
                  <td><span class="operator">ou</span></td>
                  <td>
                    Sélectionne les fiches remplissant au moins un critère sur
                    les deux. <br/><br/>
                    <u>Exemples :</u>
                    <ul>
                      <li>
                        <b>$mo : "STA AUR" ou $mo : "STA EPI"</b> -&gt;
                        Staphylocoques dorés ou blancs
                      </li>
                      <li>
                        <b>$poids &lt; 1000 ou $ageg &lt; 30</b> -&gt;
                        Les bébés de moins d'1kg ou d'âge gestationnel inférieur
                        à 30 semaines.
                      </li>
                    </ul>
                  </td>
                </tr>
                <tr>
                  <td><span class="operator">non</span></td>
                  <td>
                    Sélectionne les fiches ne remplissant pas le critère qui
                    suit.<br /><br />
                    <u>Exemples : </u>
                    <ul>
                      <li>
                        <b>non $mo ~ "STA"</b> -&gt;
                        Toutes les infections sans Staphylocoque.
                      </li>
                    </ul>
                  </td>
                </tr>
              </tbody>
            </table>
            <h4>Précédence des opérateurs et expressions complexes</h4>
            <p>
              La précédence des opérateurs se fait par ordre inverse de
              lecture, ce qui n'est pas évident à maîtriser.
            </p>
            <p>
              <strong>
                Il est donc fortement recommandé d'écrire des filtres en
                utilisant les parenthèses.
              </strong>
            </p>
            <p>
              <u>Exemples</u>
              <ul>
                <li>
                  <b>$mo ~ "STA" et ($pe : C1 ou $pe : C2)</b> -&gt;
                  Staphylocoques sur porte d'entrée CVC ou CCI
                </li>
                <li>
                  <b>non ($mo ~ "STA" et ($pe : C1 ou $pe : C2))</b> -&gt;
                  Tout sauf les Staphylocoques sur porte d'entrée CVC ou CCI
                </li>
              </ul>
            </p>
          </div>
        </collapse>
        <collapse v-model="variablesCollapse" class="row"
                  :style="{ 'margin-top': '2rem' }">
          <div class="col-md-12">
            <table class="table table-responsive">
              <tbody>
                <tr v-for="(v, k) in variables" :key="`variable-${k}`">
                  <td>${{v.key}}</td>
                  <td>{{v.name}}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </collapse>
      </div>
    </div>
    <div class="filter-sticky" v-sticky="sticky">
      <div class="filter-title">
        <span>
          Tapez «$» pour commencer.
        </span>
        <span v-if="filterSearch.length > 0">
          <br class="visible-xs">
          <b class="txt-primary"><i>Exemples : </i></b>
          <span v-for="(d, k) in filterSearch" :key="`dd-${k}`">
            <br class="visible-xs">
            <dropdown ref="dropdown" class="filter-example">
              <a role="button">
                <i>{{ d.title }}<span class="caret"></span></i>
              </a>
              <template slot="dropdown">
                <li v-for="(f, i) in d.list"
                    :class="{'divider': f.name == null}"
                    :key="`item-${k}-${i}`">
                  <a role="button"
                     v-if="f.name != null"
                     @click="filteredInfections.filter = f.filter">
                    <span class="filter-value">
                      {{ f.name }} <br><i><small>{{ f.filter }}</small></i>
                    </span>
                  </a>
                </li>
              </template>
            </dropdown>
          </span>
        </span>
      </div>
      <div>
        <sui-filter-input v-model="filteredInfections"
                          :list="list"
                          :variables="variables">
        </sui-filter-input>
        <slot name="sticky-footer">
        </slot>
      </div>
    </div>
  </div>
</template>
<script>
import suiFilterInput from '@/sui/components/filterInput'
import ice from '@/sui/tools/ice'

export default {
  name: 'sui-data-filter-input',

  props: {
    variables: {
      type: Array,
      default: () => []
    },

    filterSearch: {
      type: Array,
      default: () => []
    },

    list: {
      type: Array,
      required: true
    },

    value: {
      type: Object,
      required: true
    },

    sticky: {
      type: Boolean,
      default: true
    }
  },

  data: function () {
    return {
      filterHelp: false,
      filteredInfections: ice.freeze(this.value),
      variablesCollapse: false
    }
  },

  watch: {
    filteredInfections: function (v) {
      this.$emit('input', ice.freeze(v))
    }
  },

  components: {
    'sui-filter-input': suiFilterInput
  }
}
</script>
<style scoped lang="less">
@import '~variables';

.operator {
  border-radius: 0.4rem;
  background-color: @gray-lighter;
  padding: 0.2rem 1rem 0.5rem 1rem;
  font-weight: bold;
  color: @dark-base;
}

h3 {
  margin-top: 0rem;
}

.filter-sticky  {
  background-color: white;
  z-index: 11 !important;
}

.vue-sticky-el:not(.top-sticky) {
  width: 100% !important;
}

.top-sticky {
  border-bottom: 1px solid @base;
}

.filter-title {
  margin-bottom: 0.4rem;
}

.dropdown-menu > li > a {
  line-height: 1;
  margin-bottom: 0.3rem;
  i {
    color: @brand-primary;
  }
}

.dropdown-toggle {
  margin-right: 0.5rem;
}

.filter-example {
  margin-right: 0.5rem;
  margin-left: 0.5rem;
  margin-top: -0.4rem;
}
</style>
